import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Search, Star, MapPin } from 'lucide-react';
import { clubsService } from '../../../../../../services';
import './ClubSelector.css';

const ClubSelector = ({ selectedClub, onClubSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [favoriteClubs, setFavoriteClubs] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);
    const trimmedSearchTerm = searchTerm.trim();
    const isSearching = trimmedSearchTerm.length > 0;

    useEffect(() => {
        fetchFavoriteClubs();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchFavoriteClubs = async () => {
        try {
            const data = await clubsService.getFavoriteClubs();
            setFavoriteClubs(data);
        } catch (error) {
            console.error('Error fetching favorite clubs:', error);
        }
    };

    const handleSearch = useCallback(async (rawTerm) => {
        const trimmed = (rawTerm || '').trim();
        if (!trimmed) {
            setSearchResults([]);
            return;
        }
        setIsLoading(true);
        try {
            const data = await clubsService.searchClubs(trimmed);
            setSearchResults(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error searching clubs:', error);
            setSearchResults([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const timer = setTimeout(() => {
            handleSearch(searchTerm);
        }, 250);

        return () => clearTimeout(timer);
    }, [searchTerm, isOpen, handleSearch]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    const handleClubSelection = (club) => {
        onClubSelect(club);
        setIsOpen(false);
        setSearchTerm('');
        setSearchResults([]);
    };

    const selectedClubName = selectedClub?.name ||
        favoriteClubs.find(club => club.clubId === selectedClub?.id)?.name ||
        'Select venue';

    const dedupedSearchResults = searchResults.filter(
        (club, index, list) => list.findIndex(item => item.clubId === club.clubId) === index
    );

    const dedupedFavoriteClubs = favoriteClubs.filter(
        (club, index, list) => list.findIndex(item => item.clubId === club.clubId) === index
    );

    return (
        <div ref={dropdownRef} className="club-selector-wrap">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="club-selector-button"
            >
                <span className="club-selector-text">
                    {selectedClubName}
                </span>
                <ChevronDown className={`club-selector-chevron ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="club-selector-dropdown">
                    <div className="club-selector-search">
                        <Search size={16} />
                        <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Search venues by name"
                            />
                    </div>

                    <div className="club-selector-list">
                        {isSearching && dedupedSearchResults.length > 0 && (
                            <div className="club-selector-group">
                                <h6>
                                    <Search size={14} />
                                    Matching venues
                                </h6>
                                <ul>
                                    {dedupedSearchResults.map((club) => (
                                        <li
                                            key={club.clubId}
                                            onClick={() => handleClubSelection(club)}
                                        >
                                            <MapPin size={13} />
                                            {club.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {isSearching && dedupedFavoriteClubs.length > 0 && (
                            <div className="club-selector-group">
                                <h6>
                                    <Star size={14} />
                                    Favourite venues
                                </h6>
                                <ul>
                                    {dedupedFavoriteClubs.map((club) => (
                                        <li
                                            key={club.clubId}
                                            onClick={() => handleClubSelection(club)}
                                        >
                                            <MapPin size={13} />
                                            {club.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {!isSearching && dedupedFavoriteClubs.length > 0 && (
                            <div className="club-selector-group">
                                <h6>
                                    <Star size={14} />
                                    Favourite venues
                                </h6>
                                <ul>
                                    {dedupedFavoriteClubs.map((club) => (
                                        <li
                                            key={club.clubId}
                                            onClick={() => handleClubSelection(club)}
                                        >
                                            <MapPin size={13} />
                                            {club.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {isSearching && !isLoading && dedupedSearchResults.length === 0 && (
                            <div className="club-selector-empty">
                                No venues found for "{trimmedSearchTerm}"
                            </div>
                        )}

                        {!isSearching && dedupedFavoriteClubs.length === 0 && (
                            <div className="club-selector-empty">
                                Search to browse all venues.
                            </div>
                        )}

                        {isLoading && (
                            <div className="club-selector-empty">
                                Searching venues...
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClubSelector;
